import { useState } from 'react'
import type {
  CharacterSheet,
  HpAction,
  HpProblem,
  RollReason,
  SheetAttack,
} from '@rollcall/contracts'
import type { RollMode } from '@rollcall/rules'
import { useT } from './i18n-context.js'
import { contentName } from './labels.js'
import { ProvenanceProvider, ValueButton } from './provenance.js'

/**
 * Вкладка «Бой».
 *
 * Ни одного расчёта: КД, инициатива, скорость, хиты и бонусы атак
 * приходят посчитанными вместе с провенансом (D5), а бросок делает
 * сервер (D12). Компонент показывает и отправляет команды.
 *
 * Атаки собраны из оружия в инвентаре по правилам источника (с. 22),
 * а не по списку в компоненте: у нового оружия появится строка,
 * и трогать вкладку для этого не придётся.
 */

export type CombatTabProps = {
  readonly sheet: CharacterSheet
  readonly onHp: (action: HpAction) => void
  readonly onRest: (kind: 'long' | 'short') => void
  readonly onRoll: (
    reason: RollReason,
    options: { notation?: string | null; manual?: number | null; mode?: RollMode },
  ) => void
  /** Преимущество и помеха: переключатель общий с панелью роллера. */
  readonly mode: RollMode
  readonly onMode: (mode: RollMode) => void
  readonly busy: boolean
  readonly problems: readonly HpProblem[]
  readonly failure: string | null
}

export function CombatTab({
  sheet,
  onHp,
  onRest,
  onRoll,
  mode,
  onMode,
  busy,
  problems,
  failure,
}: CombatTabProps) {
  const t = useT()
  const { attacks } = sheet.combat
  const inHands = attacks.filter((attack) => attack.equipped)
  const carried = attacks.filter((attack) => !attack.equipped)

  return (
    <ProvenanceProvider view={{ values: sheet.values, sources: sheet.sources }}>
      <section className="app-screen" aria-labelledby="combat-title">
        <h2 id="combat-title">{t('combat.title')}</h2>

        {failure !== null && (
          <p className="app-problems" role="alert">
            {failure}
          </p>
        )}

        <div className="app-grid">
          <ValueButton path="ac" label={t('sheet.ac')} />
          <ValueButton path="initiative" label={t('sheet.initiative')} signed />
          <ValueButton path="speed" label={t('sheet.speed')} />
        </div>

        <HitPoints
          sheet={sheet}
          onHp={onHp}
          onRest={onRest}
          busy={busy}
          problems={problems}
        />

        <section aria-labelledby="combat-attacks">
          <h3 id="combat-attacks">{t('combat.attacks')}</h3>

          <fieldset className="app-slot">
            <legend className="app-slot-legend">{t('roll.mode')}</legend>
            <div className="app-slot-options">
              {(['normal', 'advantage', 'disadvantage'] as const).map((item) => (
                <label key={item} className="app-option">
                  <input
                    type="radio"
                    name="roll-mode"
                    value={item}
                    checked={mode === item}
                    onChange={() => {
                      onMode(item)
                    }}
                  />
                  <span>{t(`roll.${item}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {attacks.length === 0 ? (
            <p>{t('combat.noAttacks')}</p>
          ) : (
            <>
              <AttackGroup
                id="combat-in-hands"
                title={t('combat.inHands')}
                attacks={inHands}
                sheet={sheet}
                onRoll={onRoll}
                mode={mode}
                busy={busy}
              />
              <AttackGroup
                id="combat-carried"
                title={t('combat.carried')}
                attacks={carried}
                sheet={sheet}
                onRoll={onRoll}
                mode={mode}
                busy={busy}
              />
            </>
          )}
        </section>
      </section>
    </ProvenanceProvider>
  )
}

/**
 * Хиты: текущие, максимум, временные и кость хитов.
 *
 * Изменение — команда (D3): «получил 7 урона», а не «хитов теперь 6».
 * Поле одно на три действия: число вводится один раз, а что с ним
 * сделать, говорит кнопка.
 */
function HitPoints({
  sheet,
  onHp,
  onRest,
  busy,
  problems,
}: {
  readonly sheet: CharacterSheet
  readonly onHp: (action: HpAction) => void
  readonly onRest: (kind: 'long' | 'short') => void
  readonly busy: boolean
  readonly problems: readonly HpProblem[]
}) {
  const t = useT()
  const [amount, setAmount] = useState('')
  const { hp } = sheet.combat
  const value = Number(amount)
  const ready = amount !== '' && Number.isFinite(value) && value >= 0

  const act = (kind: HpAction['kind']) => {
    if (!ready) return
    onHp({ kind, amount: Math.trunc(value) })
    setAmount('')
  }

  return (
    <section aria-labelledby="combat-hp">
      <h3 id="combat-hp">{t('combat.hp')}</h3>

      <p className="app-hp-value" data-testid="hp-value">
        {t('combat.hpOf', { current: hp.current, max: hp.max })}
        {hp.temp > 0 && ` · ${t('combat.tempValue', { count: hp.temp })}`}
      </p>
      {hp.hitDie !== null && (
        <p className="app-slot-hint">
          {t('combat.hitDie')}:{' '}
          {t('combat.hitDiceValue', { count: hp.hitDice, die: hp.hitDie })}
        </p>
      )}

      <div className="app-hp-actions">
        <label className="app-field app-field-narrow">
          <span>{t('combat.amount')}</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value)
            }}
          />
        </label>
        <div className="app-actions">
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy || !ready}
            onClick={() => {
              act('damage')
            }}
          >
            {t('combat.damageAction')}
          </button>
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy || !ready}
            onClick={() => {
              act('heal')
            }}
          >
            {t('combat.healAction')}
          </button>
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy || !ready}
            onClick={() => {
              act('temp')
            }}
          >
            {t('combat.tempAction')}
          </button>
        </div>
      </div>

      {problems.map((problem) => (
        <p key={problem.code} className="app-problems" role="alert">
          {t(`combat.refusal.${problem.code}`)}
        </p>
      ))}

      {/* Отдых — два разных дела, и мешать их в один ряд кнопок
          значит обещать, что короткий тоже лечит. Он не лечит:
          лечит потраченная кость хитов (с. 187), и кости стоят
          рядом с кнопкой, вместе с остатком. */}
      <div className="app-rest">
        <div className="app-actions">
          <button
            type="button"
            className="app-button"
            disabled={busy}
            onClick={() => {
              onRest('long')
            }}
          >
            {t('combat.rest')}
          </button>
          <span className="app-slot-hint">{t('combat.restHint')}</span>
        </div>

        <div className="app-actions">
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy}
            onClick={() => {
              onRest('short')
            }}
          >
            {t('combat.shortRest')}
          </button>
          <span className="app-slot-hint">{t('combat.shortRestHint')}</span>
        </div>

        {/* Кость хитов тратят в короткий отдых: бросок делает сервер,
            и он же кладёт его в журнал бросков (с. 187, D12).
            Ограничение — кости, а не число отдыхов: их столько,
            сколько уровней. */}
        <div className="app-actions">
          <button
            type="button"
            className="app-button"
            data-testid="spend-die"
            disabled={busy || hp.hitDiceSpent >= hp.hitDice || hp.current >= hp.max}
            onClick={() => {
              onHp({ kind: 'spend_die', amount: null })
            }}
          >
            {t('combat.spendDie', {
              left: hp.hitDice - hp.hitDiceSpent,
              die: hp.hitDie ?? '',
            })}
          </button>
          <span className="app-slot-hint">
            {hp.current >= hp.max
              ? t('combat.spendDieFull')
              : hp.hitDiceSpent >= hp.hitDice
                ? t('combat.spendDieNone')
                : t('combat.spendDieHint', { die: hp.hitDie ?? '' })}
          </span>
        </div>
      </div>
    </section>
  )
}

function AttackGroup({
  id,
  title,
  attacks,
  sheet,
  onRoll,
  mode,
  busy,
}: {
  readonly id: string
  readonly title: string
  readonly attacks: readonly SheetAttack[]
  readonly sheet: CharacterSheet
  readonly onRoll: CombatTabProps['onRoll']
  readonly mode: RollMode
  readonly busy: boolean
}) {
  if (attacks.length === 0) return null

  return (
    <section aria-labelledby={id}>
      <h4 id={id} className="app-group-title">
        {title}
      </h4>
      <ul className="app-list app-attacks">
        {attacks.map((attack) => (
          <Attack
            key={attack.id}
            attack={attack}
            sheet={sheet}
            onRoll={onRoll}
            mode={mode}
            busy={busy}
          />
        ))}
      </ul>
    </section>
  )
}

/**
 * Строка атаки: бонус и урон числами с разбором по тапу, рядом —
 * кнопки бросков. Кости урона показываются записью из предмета
 * («1d8»): их бросает роллер.
 */
function Attack({
  attack,
  sheet,
  onRoll,
  mode,
  busy,
}: {
  readonly attack: SheetAttack
  readonly sheet: CharacterSheet
  readonly onRoll: CombatTabProps['onRoll']
  readonly mode: RollMode
  readonly busy: boolean
}) {
  const t = useT()
  const name = contentName(t, attack.id, attack.name)
  const bonus =
    attack.damagePath === null ? null : sheet.values[attack.damagePath]?.value
  const damageBonus =
    typeof bonus === 'number' && bonus !== 0
      ? `${bonus > 0 ? '+' : '−'}${Math.abs(bonus)}`
      : ''

  return (
    <li className="app-attack" data-attack={attack.id}>
      <div className="app-inventory-head">
        <span className="app-value-label">{name}</span>
        <span className="app-value-note">
          {t(attack.ranged ? 'combat.ranged' : 'combat.melee')}
          {' · '}
          {attack.spell
            ? t('combat.spellAttack')
            : t(attack.proficient ? 'combat.proficient' : 'combat.noProficiency')}
        </span>
      </div>

      {/* Две карточки в строку и ровно пополам: `auto-fit` разносил
          их по длине содержимого, и у заговора, где вместо разбора
          стоят одни кости, «Атака» растягивалась на всю ширину. */}
      <div className="app-grid app-attack-values">
        <ValueButton path={attack.attackPath} label={t('combat.attack')} signed />
        {attack.damagePath === null ? (
          // У заговора прибавки к урону нет: показываются одни кости,
          // и разбирать в них нечего.
          <p className="app-value-button" data-static="">
            <span className="app-value-label">{t('combat.damage')}</span>
            <span className="app-value-number">{attack.damage.notation}</span>
            <span className="app-value-note">
              {t(`trait.damage.${attack.damage.type}`, {
                defaultValue: attack.damage.type,
              })}
            </span>
          </p>
        ) : (
          <ValueButton
            path={attack.damagePath}
            label={t('combat.damage')}
            display={`${attack.damage.notation}${damageBonus}`}
            note={t(`trait.damage.${attack.damage.type}`, {
              defaultValue: attack.damage.type,
            })}
          />
        )}
      </div>
      {attack.damage.versatile !== null && (
        <p className="app-slot-hint">
          {t('combat.versatile', { notation: attack.damage.versatile })}
        </p>
      )}

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy}
          onClick={() => {
            onRoll({ kind: 'attack', subject: attack.id }, { mode })
          }}
        >
          {t('combat.rollAttack')}
        </button>
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy}
          onClick={() => {
            // Урон бросают одной кистью: преимущество и помеха
            // относятся к попаданию, а не к нему (с. 8).
            onRoll({ kind: 'damage', subject: attack.id }, {})
          }}
        >
          {t('combat.rollDamage')}
        </button>
      </div>
    </li>
  )
}
